import React from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import { AppText } from "@toothfairy/shared-ui";
import AdminInfoLeaf from "../Components/SubComponents/AdminInfoLeaf";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import AppForms from "@toothfairy/shared-ui/AppForms";
import { copyToClipboard } from "@toothfairy/shared-ui/ClipboardUtils";
import { isLargeScreen } from "../../MainViewContainer";
import Constants from "../../../Constants";

const SingleSignOnScreen = ({ onSubmit = () => {}, isSaveInProgress }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { getActiveWorkspace } = useWorkspaces();
  const AppToasty = AppToast.useToast();
  const ssoValidationSchema = AppForms.yup.object().shape({
    ssoType: AppForms.yup.string().required().oneOf(["Azure"]),
    federationMetadataUrl: AppForms.yup.string().when("ssoType", {
      is: "Azure",
      then: AppForms.yup
        .string()
        .matches(
          /^https:\/\/login\.microsoftonline\.com\/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}\/federationmetadata\/2007-06\/federationmetadata\.xml$/,
          t("admin.sso.validation.invalidAzureUrl"),
        )
        .required(t("admin.sso.validation.providerRequired")),
      otherwise: AppForms.yup.string().required(),
    }),
    ssoDomain: AppForms.yup
      .string()
      .required()
      .matches(
        "^(?!-)[A-Za-z0-9-]{1,63}(?<!-).([A-Za-z]{2,6}(.?[A-Za-z]{2,3})?)$",
        t("admin.sso.validation.invalidDomain"),
      ),
  });

  return (
    <View
      style={{
        marginHorizontal: "auto",
        paddingHorizontal: 20,
        paddingVertical: 10,
        height: 340,
        maxWidth: 550,
      }}
    >
      <AppForms.AppForm
        style={{
          minWidth: Platform.OS === "web" && isLargeScreen() ? 350 : 250,
          maxWidth: !isLargeScreen() && 250,
          alignSelf: !isLargeScreen() && "center",
        }}
        initialValues={{
          federationMetadataUrl:
            getActiveWorkspace()?.federationMetadataUrl || "",
          ssoType: getActiveWorkspace()?.ssoType || "",
          ssoDomain: getActiveWorkspace()?.ssoDomain || "",
        }}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={ssoValidationSchema}
      >
        <AppForms.AppFormDropDown
          formFieldContainerStyle={{
            flex: 1,
          }}
          isElevated={false}
          dropDownStyle={{
            borderColor: currentTheme?.theme?.border_color,
            maxWidth: !isLargeScreen() && 250,
          }}
          inputLabelStyle={{
            marginLeft: 5,
          }}
          showTextInputLabel
          label={t("admin.sso.providerLabel")}
          isMandatory
          name="ssoType"
          values={["None", "Azure"]}
          items={["None", "Azure"]}
        />
        <AppForms.AppFormField
          formFieldContainerStyle={{
            marginTop: 10,
          }}
          showTextInputLabel
          label={t("admin.sso.federationMetadataUrlLabel")}
          isMandatory
          name="federationMetadataUrl"
        />
        <AppText
          color={currentTheme?.theme?.darkGrey}
          fontSize={12}
          wrapperStyle={{ marginTop: 2, marginHorizontal: 4, marginBottom: 4 }}
        >
          {t("admin.sso.federationMetadataUrlDescription")}
        </AppText>
        <AppForms.AppFormField
          formFieldContainerStyle={{
            marginTop: 10,
          }}
          showTextInputLabel
          label={t("admin.sso.domainMappingLabel")}
          isMandatory
          name="ssoDomain"
        />
        <AppText
          color={currentTheme?.theme?.darkGrey}
          fontSize={12}
          wrapperStyle={{ marginTop: 2, marginHorizontal: 4, marginBottom: 4 }}
        >
          {t("admin.sso.domainMappingDescription")}
        </AppText>
        <View
          style={{
            marginHorizontal: 10,
          }}
        >
          <AdminInfoLeaf
            wrapperStyle={{
              alignSelf: "flex-start",
              width: "100%",
              alignItems: "flex-start",
            }}
            titleColor={currentTheme?.theme?.primary}
            showSimpleText
            subCaptionColor={currentTheme?.theme?.primary}
            title={t("admin.sso.entityIdTitle")}
            accessibilityLabel={"entity_id"}
            simpleTextWrapperStyle={{ maxWidth: "100%" }}
            simpleTextStyle={{ flexShrink: 1, wordBreak: "break-all" }}
            value={"urn:amazon:cognito:sp:ap-southeast-2_ZdZ1v3tsr"}
            handleClick={async () => {
              const success = await copyToClipboard(
                "urn:amazon:cognito:sp:ap-southeast-2_ZdZ1v3tsr",
              );
              if (success) {
                AppToasty.show(t("admin.sso.copied"), { type: "success" });
              }
            }}
          />
          <AdminInfoLeaf
            wrapperStyle={{
              alignSelf: "flex-start",
              width: "100%",
              alignItems: "flex-start",
            }}
            titleColor={currentTheme?.theme?.primary}
            showSimpleText
            subCaptionColor={currentTheme?.theme?.primary}
            title={t("admin.sso.replyUrlTitle")}
            accessibilityLabel={"reply_url"}
            simpleTextWrapperStyle={{ maxWidth: "100%" }}
            simpleTextStyle={{
              flexShrink: 1,
              wordBreak: "break-all",
              width: 300,
            }}
            value={
              "https://toothfairyai808525a0-custom-dev.auth.ap-southeast-2.amazoncognito.com/saml2/idpresponse"
            }
            handleClick={async () => {
              const success = await copyToClipboard(
                "https://toothfairyai808525a0-custom-dev.auth.ap-southeast-2.amazoncognito.com/saml2/idpresponse",
              );
              if (success) {
                AppToasty.show(t("admin.sso.copied"), { type: "success" });
              }
            }}
          />
        </View>

        <AppForms.AppSubmitButton
          title={t("admin.sso.saveButton")}
          isDisabled={isSaveInProgress}
          awesomeIconColor={currentTheme?.theme?.white}
          backgroundColor={currentTheme?.theme?.primary}
          btnContentStyle={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
          style={[
            {
              height: Constants.appButtonHeight,
              width: "auto",
              shadowColor: currentTheme?.theme?.transparent,
              marginHorizontal: 10,
            },
          ]}
          // btnLabelStyle={{ fontSize: 16 }}
          wrapperStyle={[
            {
              width: "fit-content",
              minWidth: 150,
              marginTop: 30,
              alignSelf: "center",
              height: Constants.appButtonHeight,
            },
          ]}
        />
      </AppForms.AppForm>
    </View>
  );
};

export default SingleSignOnScreen;
